import { create } from 'zustand';
import type { ToastMessage, LoadingState } from '@/types';

interface UiState {
  // Toast notifications
  toasts: ToastMessage[];
  addToast: (toast: Omit<ToastMessage, 'id'>) => void;
  removeToast: (id: string) => void;
  clearToasts: () => void;

  // Global loading state
  loadingState: LoadingState;
  setLoading: (isLoading: boolean, message?: string) => void;

  // Sidebar state
  isSidebarOpen: boolean;
  toggleSidebar: () => void;
  setSidebarOpen: (isOpen: boolean) => void;

  // Modal state
  modals: Record<string, boolean>;
  openModal: (modalId: string) => void;
  closeModal: (modalId: string) => void;
  toggleModal: (modalId: string) => void;
}

let toastIdCounter = 0;

export const useUiStore = create<UiState>((set) => ({
  // Toast state
  toasts: [],

  addToast: (toast) =>
    set((state) => {
      const id = `toast-${++toastIdCounter}`;
      const newToast: ToastMessage = {
        ...toast,
        id,
        duration: toast.duration || 3000,
      };

      // Auto-remove toast after duration
      setTimeout(() => {
        set((state) => ({
          toasts: state.toasts.filter((t) => t.id !== id),
        }));
      }, newToast.duration);

      return {
        toasts: [...state.toasts, newToast],
      };
    }),

  removeToast: (id) =>
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    })),

  clearToasts: () =>
    set({
      toasts: [],
    }),

  // Loading state
  loadingState: {
    isLoading: false,
    message: undefined,
  },

  setLoading: (isLoading, message) =>
    set({
      loadingState: {
        isLoading,
        message,
      },
    }),

  // Sidebar state
  isSidebarOpen: true,

  toggleSidebar: () =>
    set((state) => ({
      isSidebarOpen: !state.isSidebarOpen,
    })),

  setSidebarOpen: (isOpen) =>
    set({
      isSidebarOpen: isOpen,
    }),

  // Modal state
  modals: {},

  openModal: (modalId) =>
    set((state) => ({
      modals: {
        ...state.modals,
        [modalId]: true,
      },
    })),

  closeModal: (modalId) =>
    set((state) => ({
      modals: {
        ...state.modals,
        [modalId]: false,
      },
    })),

  toggleModal: (modalId) =>
    set((state) => ({
      modals: {
        ...state.modals,
        [modalId]: !state.modals[modalId],
      },
    })),
}));

// Convenience hooks for common UI actions
export const useToast = () => {
  const addToast = useUiStore((state) => state.addToast);

  return {
    success: (message: string, duration?: number) =>
      addToast({ type: 'success', message, duration }),
    error: (message: string, duration?: number) =>
      addToast({ type: 'error', message, duration }),
    warning: (message: string, duration?: number) =>
      addToast({ type: 'warning', message, duration }),
    info: (message: string, duration?: number) =>
      addToast({ type: 'info', message, duration }),
  };
};
